'use client';

import { useState, useEffect } from 'react';
import SearchBar from './SearchBar';
import FilterBar, { FilterState } from './FilterBar';
import StoryCard from './StoryCard';
import CategorySection from './CategorySection';
import CreatorSpotlight from './CreatorSpotlight';
import RegionalCollection from './RegionalCollection';
import TrendingSection from './TrendingSection';
import Icon from '@/components/ui/AppIcon';

interface Story {
  id: number;
  title: string;
  creator: string;
  category: string;
  language: string;
  duration: string;
  plays: string;
  rating: number;
  image: string;
  alt: string;
  description: string;
  mood: string;
}

interface Creator {
  id: number;
  name: string;
  bio: string;
  image: string;
  alt: string;
  followers: string;
  stories: number;
  rating: number;
  badge: string;
}

interface RegionalCollectionType {
  id: number;
  title: string;
  region: string;
  language: string;
  storyCount: number;
  image: string;
  alt: string;
  description: string;
}

const StoriesLibraryInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    category: 'All Stories',
    language: 'All Languages',
    mood: 'All Moods',
    duration: 'All Durations',
    searchQuery: ''
  });
  const [bookmarkedStories, setBookmarkedStories] = useState<number[]>([]);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockStories: Story[] = [
  {
    id: 1,
    title: 'Dil Ki Baatein',
    creator: 'Priya Sharma',
    category: 'Romance',
    language: 'Hindi',
    duration: '25 mins',
    plays: '1.2M',
    rating: 4.8,
    image: "https://images.unsplash.com/photo-1731998299095-a0d3d139b297",
    alt: 'Young Indian couple holding hands while walking in sunset with traditional architecture in background',
    description: 'A heartwarming tale of love that transcends boundaries and brings two souls together in the bustling streets of Mumbai.',
    mood: 'Romantic'
  },
  {
    id: 2,
    title: 'Raat Ka Rahasya',
    creator: 'Vikram Singh',
    category: 'Thriller',
    language: 'Hindi',
    duration: '35 mins',
    plays: '890K',
    rating: 4.6,
    image: "https://images.unsplash.com/photo-1726804406366-35768a558f4b",
    alt: 'Dark mysterious alleyway at night with dim streetlights and shadows creating suspenseful atmosphere',
    description: 'A spine-chilling mystery unfolds as a detective races against time to solve a series of unexplained disappearances.',
    mood: 'Suspenseful'
  },
  {
    id: 3,
    title: 'Hasya Katha',
    creator: 'Amit Tandon',
    category: 'Comedy',
    language: 'Hindi',
    duration: '15 mins',
    plays: '2.1M',
    rating: 4.9,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1934e1ac4-1767577709861.png",
    alt: 'Group of friends laughing together at outdoor cafe with colorful decorations and festive atmosphere',
    description: 'Hilarious anecdotes from everyday Indian life that will leave you in splits with relatable humor and witty observations.',
    mood: 'Humorous'
  },
  {
    id: 4,
    title: 'Kadhaigal',
    creator: 'Lakshmi Menon',
    category: 'Regional',
    language: 'Tamil',
    duration: '28 mins',
    plays: '650K',
    rating: 4.7,
    image: "https://images.unsplash.com/photo-1731159623351-d3ad4f1f94c0",
    alt: 'Traditional Tamil woman in colorful silk saree standing near ancient temple with intricate stone carvings',
    description: 'Traditional Tamil folklore brought to life with captivating narration and authentic cultural elements.',
    mood: 'Inspiring'
  },
  {
    id: 5,
    title: 'Bhoot Bangla',
    creator: 'Rajesh Kumar',
    category: 'Horror',
    language: 'Hindi',
    duration: '40 mins',
    plays: '1.5M',
    rating: 4.5,
    image: "https://images.unsplash.com/photo-1639334166438-3e7531d96a90",
    alt: 'Abandoned old mansion at night with broken windows and eerie moonlight casting long shadows',
    description: 'Enter the haunted mansion where spirits from the past seek revenge and every corner hides a terrifying secret.',
    mood: 'Suspenseful'
  },
  {
    id: 6,
    title: 'Prem Kahani',
    creator: 'Neha Kapoor',
    category: 'Romance',
    language: 'Hindi',
    duration: '30 mins',
    plays: '980K',
    rating: 4.8,
    image: "https://images.unsplash.com/photo-1539932525408-b8cf76cd5b63",
    alt: 'Romantic couple sitting on bench under blooming cherry blossom tree with pink petals falling around them',
    description: 'A beautiful love story set in the picturesque valleys of Kashmir, where destiny brings two hearts together.',
    mood: 'Romantic'
  },
  {
    id: 7,
    title: 'Ramayana Retold',
    creator: 'Dr. Suresh Iyer',
    category: 'Mythology',
    language: 'English',
    duration: '45 mins',
    plays: '1.8M',
    rating: 4.9,
    image: "https://images.unsplash.com/photo-1665824791931-b9e6266e29e1",
    alt: 'Ancient Indian temple with golden statues of Lord Rama and intricate religious artwork depicting epic scenes',
    description: 'Experience the timeless epic of Ramayana with modern storytelling techniques and profound philosophical insights.',
    mood: 'Inspiring'
  },
  {
    id: 8,
    title: 'Tech Talks',
    creator: 'Arjun Mehta',
    category: 'Biography',
    language: 'English',
    duration: '20 mins',
    plays: '750K',
    rating: 4.6,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc43cc1b-1764658961980.png",
    alt: 'Modern tech entrepreneur presenting at conference with large screens showing innovative technology and engaged audience',
    description: 'Inspiring stories of Indian tech entrepreneurs who revolutionized the digital landscape and built global empires.',
    mood: 'Motivational'
  }];


  const mockCategories = [
  {
    title: 'Romance',
    description: 'Heartwarming love stories that touch your soul and celebrate the beauty of relationships.',
    icon: 'HeartIcon',
    storyCount: 1250
  },
  {
    title: 'Thriller',
    description: 'Edge-of-your-seat mysteries and suspenseful tales that keep you guessing until the end.',
    icon: 'BoltIcon',
    storyCount: 890
  },
  {
    title: 'Comedy',
    description: 'Hilarious stories and stand-up performances that guarantee laughter and joy.',
    icon: 'FaceSmileIcon',
    storyCount: 2100
  },
  {
    title: 'Mythology',
    description: 'Ancient Indian epics and mythological tales retold for modern audiences.',
    icon: 'SparklesIcon',
    storyCount: 650
  }];


  const mockCreators: Creator[] = [
  {
    id: 1,
    name: 'Priya Sharma',
    bio: 'Award-winning storyteller specializing in contemporary romance and emotional narratives.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f10a419e-1763296115132.png",
    alt: 'Professional Indian woman with long black hair wearing elegant blue blazer smiling warmly at camera',
    followers: '250K',
    stories: 45,
    rating: 4.8,
    badge: 'Verified'
  },
  {
    id: 2,
    name: 'Vikram Singh',
    bio: 'Master of suspense and thriller genres with over 100 gripping audio stories.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5ea51b0-1763293852695.png",
    alt: 'Confident Indian man with short beard wearing casual denim shirt in modern studio setting',
    followers: '180K',
    stories: 67,
    rating: 4.7,
    badge: 'Verified'
  },
  {
    id: 3,
    name: 'Dr. Suresh Iyer',
    bio: 'Renowned mythologist bringing ancient Indian wisdom to contemporary audiences.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_195a0bc5b-1767781484280.png",
    alt: 'Distinguished elderly Indian scholar with grey hair and glasses in traditional white kurta holding ancient manuscript',
    followers: '320K',
    stories: 89,
    rating: 4.9,
    badge: 'Verified'
  }];


  const mockRegionalCollections: RegionalCollectionType[] = [
  {
    id: 1,
    title: 'Tamil Nadu Tales',
    region: 'Tamil Nadu',
    language: 'Tamil',
    storyCount: 450,
    image: "https://images.unsplash.com/photo-1680707330913-919ddaae184c",
    alt: 'Ancient Tamil temple with towering gopuram covered in colorful sculptures against blue sky',
    description: 'Rich collection of Tamil folklore, classical literature, and contemporary stories from the heart of South India.'
  },
  {
    id: 2,
    title: 'Bengali Classics',
    region: 'West Bengal',
    language: 'Bengali',
    storyCount: 380,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14042b8a6-1766561655129.png",
    alt: 'Traditional Bengali woman in red and white saree standing near Howrah Bridge with Ganges river in background',
    description: 'Timeless Bengali literature and modern narratives celebrating the cultural richness of Bengal.'
  },
  {
    id: 3,
    title: 'Punjabi Vibes',
    region: 'Punjab',
    language: 'Punjabi',
    storyCount: 290,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13856f894-1765995048655.png",
    alt: 'Vibrant Punjabi festival celebration with people in colorful traditional attire dancing bhangra in golden wheat fields',
    description: 'Energetic Punjabi stories filled with humor, emotion, and the spirit of Punjab.'
  }];


  const mockTrends = [
  {
    id: 1,
    title: 'Dil Ki Baatein',
    category: 'Romance',
    trendingRank: 1,
    growthPercentage: '+45%'
  },
  {
    id: 2,
    title: 'Hasya Katha',
    category: 'Comedy',
    trendingRank: 2,
    growthPercentage: '+38%'
  },
  {
    id: 3,
    title: 'Ramayana Retold',
    category: 'Mythology',
    trendingRank: 3,
    growthPercentage: '+32%'
  },
  {
    id: 4,
    title: 'Bhoot Bangla',
    category: 'Horror',
    trendingRank: 4,
    growthPercentage: '+28%'
  },
  {
    id: 5,
    title: 'Tech Talks',
    category: 'Biography',
    trendingRank: 5,
    growthPercentage: '+25%'
  }];


  const handleFilterChange = (filters: FilterState) => {
    setActiveFilters(filters);
  };

  const handleSearch = (query: string) => {
    setActiveFilters({ ...activeFilters, searchQuery: query });
  };

  const handlePlayStory = (storyId: number) => {
    setCurrentlyPlaying(currentlyPlaying === storyId ? null : storyId);
  };

  const handleBookmarkStory = (storyId: number) => {
    setBookmarkedStories((prev) =>
    prev.includes(storyId) ?
    prev.filter((id) => id !== storyId) :
    [...prev, storyId]
    );
  };

  const filteredStories = mockStories.filter((story) => {
    const matchesCategory =
    activeFilters.category === 'All Stories' ||
    story.category === activeFilters.category;
    const matchesLanguage =
    activeFilters.language === 'All Languages' ||
    story.language === activeFilters.language;
    const matchesMood =
    activeFilters.mood === 'All Moods' || story.mood === activeFilters.mood;
    const matchesSearch =
    !activeFilters.searchQuery ||
    story.title.toLowerCase().includes(activeFilters.searchQuery.toLowerCase()) ||
    story.creator.toLowerCase().includes(activeFilters.searchQuery.toLowerCase()) ||
    story.description.toLowerCase().includes(activeFilters.searchQuery.toLowerCase());

    return matchesCategory && matchesLanguage && matchesMood && matchesSearch;
  });

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="w-full pt-24 pb-16 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-12 bg-muted rounded-lg w-1/3" />
              <div className="h-64 bg-muted rounded-xl" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((i) =>
                <div key={i} className="h-96 bg-muted rounded-xl" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="w-full pt-24 pb-16 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary mb-4">
            Stories Library
          </h1>
          <p className="text-lg text-text-secondary max-w-3xl">
            Discover thousands of captivating audio stories across genres, languages, and moods. Your perfect story awaits.
          </p>
        </div>

        <SearchBar onSearch={handleSearch} initialValue={activeFilters.searchQuery} />

        <FilterBar onFilterChange={handleFilterChange} activeFilters={activeFilters} />

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="BookOpenIcon" size={24} className="text-primary" />
            <h2 className="text-2xl font-heading font-bold text-text-primary">
              {filteredStories.length} Stories Found
            </h2>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all duration-200 ${
              viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'bg-muted text-text-primary hover:bg-primary/20'}`
              }
              aria-label="Grid view">

              <Icon name="Squares2X2Icon" size={20} />
            </button>

            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all duration-200 ${
              viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'bg-muted text-text-primary hover:bg-primary/20'}`
              }
              aria-label="List view">

              <Icon name="ListBulletIcon" size={20} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-12">
          <div className="lg:col-span-3">
            <div
              className={`grid gap-6 ${
              viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`
              }>

              {filteredStories.map((story) =>
              <StoryCard
                key={story.id}
                story={story}
                onPlay={handlePlayStory}
                onBookmark={handleBookmarkStory}
                isBookmarked={bookmarkedStories.includes(story.id)} />

              )}
            </div>

            {filteredStories.length === 0 &&
            <div className="text-center py-16">
                <Icon name="MagnifyingGlassIcon" size={64} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
                  No Stories Found
                </h3>
                <p className="text-text-secondary mb-6">
                  Try adjusting your filters or search query to find more stories.
                </p>
                <button
                onClick={() =>
                setActiveFilters({
                  category: 'All Stories',
                  language: 'All Languages',
                  mood: 'All Moods',
                  duration: 'All Durations',
                  searchQuery: ''
                })
                }
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary-dark transition-all duration-200">

                  Clear All Filters
                </button>
              </div>
            }
          </div>

          <div className="space-y-6">
            <TrendingSection trends={mockTrends} />

            <div className="bg-card rounded-xl shadow-warm p-6">
              <h3 className="text-xl font-heading font-bold text-text-primary mb-4">
                Featured Creators
              </h3>
              <div className="space-y-4">
                {mockCreators.map((creator) =>
                <CreatorSpotlight key={creator.id} creator={creator} />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-heading font-bold text-text-primary">
              Browse by Category
            </h2>
            <button className="flex items-center space-x-2 text-primary font-medium hover:text-primary-dark transition-colors duration-200">
              <span>View All</span>
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockCategories.map((category, index) =>
            <CategorySection
              key={index}
              title={category.title}
              description={category.description}
              icon={category.icon}
              storyCount={category.storyCount} />

            )}
          </div>
        </div>

        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-heading font-bold text-text-primary">
              Regional Collections
            </h2>
            <button className="flex items-center space-x-2 text-primary font-medium hover:text-primary-dark transition-colors duration-200">
              <span>Explore All Regions</span>
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockRegionalCollections.map((collection) =>
            <RegionalCollection key={collection.id} collection={collection} />
            )}
          </div>
        </div>

        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 md:p-12 text-center">
          <Icon name="SparklesIcon" size={48} className="mx-auto text-white mb-4" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
            Download the StoryNest-FM app to access our complete library of over 10,000 stories in 15+ Indian languages.
          </p>
          <button className="px-8 py-4 bg-white text-primary rounded-lg font-cta font-bold text-lg hover:bg-white/90 transition-all duration-200 inline-flex items-center space-x-2">
            <Icon name="ArrowDownTrayIcon" size={24} />
            <span>Download App Now</span>
          </button>
        </div>
      </div>
    </div>);

};

export default StoriesLibraryInteractive;